/**
 * SlottedSection — the HANDOFF shape between the aircraft control-surface (CS) designer and the
 * high-lift foil tool (FXC control-surface fold, B4 tranche).
 *
 * The CS designer computes a 2-D cut section (`CsShapeResult`, see `csEngine.ts`): the wing box
 * forward of the cut (with its cove + fillets) and the deflected control-surface element aft of
 * it, all in the SECTION'S OWN coordinate units (mm of the source geometry — NOT chords). This
 * module adapts that result into:
 *
 *   1. {@link SlottedSection} — a solver/tool-neutral two-element section record (the seed the
 *      high-lift tool consumes via `foil/sectionSeed.ts`), and
 *   2. {@link csSlotHealth} — the SAME slot-geometry health metrics the high-lift tool already
 *      computes for its own slots (`gapMetrics.slotMetrics`), evaluated on the CS cut: upstream =
 *      the wing contour, downstream = the DEFLECTED control surface. Contours are chord-normalized
 *      first so the returned {@link SlotMetrics} are in chords, directly comparable with the
 *      high-lift tool's slot_* outputs.
 *
 * Pure functions of the shape result — no React, no DOM, no network.
 */
import type { CsPoint, CsShapeResult } from './csEngine';
import { slotMetrics, type SlotMetrics } from './gapMetrics';
import type { V2 } from './estolConfig';

/**
 * OPTIONAL cut-rig provenance a publisher may attach to a section: the CS designer's own control
 * params (span band, hinge chord fraction, hinge height fraction, gap/fillet in true millimetres
 * plus the STEP's unitMm scale). Not derivable from the contours alone (`CsShapeResult` carries no `x_le` / eta band), so
 * when present these feed the exact `cs_*` provenance axes on persisted runs (see
 * `webgpu-solver/webgpuPersist.ts`); when absent those axes stay honestly null.
 */
export interface SlottedSectionRig {
  /** Inboard span fraction of the control (eta at the inboard end). */
  eta1?: number;
  /** Outboard span fraction of the control. */
  eta2?: number;
  /** Hinge chordwise fraction (x/c) of the cut. */
  xc?: number;
  /** Hinge height fraction between the local lower and upper surfaces (0 = lower, 1 = upper). */
  yh?: number;
  /** Slot gap in TRUE millimetres (the designer's slider value). */
  gapMm?: number;
  /** Cove-junction fillet radius in TRUE millimetres. */
  filletMm?: number;
  /** Millimetres per model unit of the source STEP (1 = mm-authored, 1000 = metres). Converts gapMm/filletMm into the section's own units. */
  unitMm?: number;
}

/** A two-element (wing + deflected control surface) cut section, in SECTION units (mm). */
export interface SlottedSection {
  /** Which lifting surface the section was cut from. */
  surface: 'wing' | 'htail' | 'vtail' | 'canard';
  /** Control-surface type label (e.g. 'aileron', 'elevator', 'rudder', 'flap'). */
  csType: string;
  /** Human name of the control (e.g. 'aileron L'). */
  name: string;
  /** Span fraction the section was sampled at. */
  eta: number;
  /** Local chord in section units (mm). */
  chord: number;
  /** Hinge point in section coords (mm; x aft, z up). */
  hinge: { x: number; z: number };
  /** Deflection about the hinge (deg). POSITIVE = trailing edge DOWN. */
  deflectionDeg: number;
  /** The two element contours, in section coords: the wing box and the DEFLECTED control surface. */
  elements: { id: 'wing' | 'cs'; contour: CsPoint[] }[];
  /** Optional cut-rig provenance (the CS designer's own control params) — see {@link SlottedSectionRig}. */
  rig?: SlottedSectionRig;
}

/**
 * Convert a `{x[], z[]}` contour to `[x, z]` point pairs, dropping non-finite / ragged entries.
 * Local (runtime-independent) mirror of `csEngine.contourToPoints`, so this module stays loadable
 * while the engine lands and never fabricates points from malformed arrays.
 */
function contourPoints(c: { x: number[]; z: number[] } | null | undefined): CsPoint[] {
  if (!c || !Array.isArray(c.x) || !Array.isArray(c.z)) return [];
  const n = Math.min(c.x.length, c.z.length);
  const out: CsPoint[] = [];
  for (let i = 0; i < n; i++) {
    const x = c.x[i];
    const z = c.z[i];
    if (typeof x === 'number' && typeof z === 'number' && Number.isFinite(x) && Number.isFinite(z)) {
      out.push([x, z]);
    }
  }
  return out;
}

/**
 * Adapt a computed CS cut ({@link CsShapeResult}) into the {@link SlottedSection} handoff record.
 * The wing element carries `wing_contour` (the coved wing box) and the cs element carries
 * `cs_contour_defl` (the control surface at its DEFLECTED pose — the pose whose slot matters).
 * Optional `rig` provenance is passed through verbatim when the caller has it.
 */
export function toSlottedSection(
  shape: CsShapeResult,
  meta: {
    surface: SlottedSection['surface'];
    csType: string;
    name: string;
    deflectionDeg: number;
    rig?: SlottedSectionRig;
  },
): SlottedSection {
  return {
    surface: meta.surface,
    csType: meta.csType,
    name: meta.name,
    eta: shape.eta,
    chord: shape.chord,
    hinge: { x: shape.hinge_x, z: shape.hinge_z },
    deflectionDeg: meta.deflectionDeg,
    elements: [
      { id: 'wing', contour: contourPoints(shape.wing_contour) },
      { id: 'cs', contour: contourPoints(shape.cs_contour_defl) },
    ],
    ...(meta.rig ? { rig: meta.rig } : {}),
  };
}

/** Chord-normalize a section-unit contour to the `[x, y]` V2 pairs `slotMetrics` consumes
 *  (the section's z-up maps onto the tool's y-up; both are "x aft"). Translation is irrelevant
 *  to every slot metric (they are all differences), so dividing by chord is sufficient. */
function toChordV2(points: CsPoint[], chord: number): V2[] {
  return points.map((p) => [p[0] / chord, p[1] / chord] as V2);
}

/**
 * Slot-geometry health of a CS cut, on the high-lift tool's OWN metric vocabulary: upstream =
 * `wing_contour` (the coved wing box), downstream = `cs_contour_defl` (the deflected control
 * surface whose nose sits in the slot). Returns CHORD-NORMALIZED {@link SlotMetrics} (gap /
 * overlap / exitWidth in chords, curvature in chord^-1) so the values are directly comparable to
 * the tool's `slot_*` outputs. Degenerate inputs (short/empty contours, a non-positive chord, or
 * a non-finite resulting throat) return `null` — honest-absent, never fabricated.
 */
export function csSlotHealth(shape: CsShapeResult): SlotMetrics | null {
  const chord = shape.chord;
  if (typeof chord !== 'number' || !Number.isFinite(chord) || chord <= 0) return null;
  const wing = contourPoints(shape.wing_contour);
  const cs = contourPoints(shape.cs_contour_defl);
  // Same >= 3-vertex floor gapMetrics.orderByLeadingEdge applies to a usable wall contour.
  if (wing.length < 3 || cs.length < 3) return null;
  const m = slotMetrics(toChordV2(wing, chord), toChordV2(cs, chord));
  if (!Number.isFinite(m.gap) || !Number.isFinite(m.overlap)) return null;
  return m;
}

/**
 * Hinge chordwise fraction (x/c) recovered from a section's own geometry: the leading edge is the
 * min-x vertex over both element contours, and the hinge fraction is measured from it against the
 * stored chord. `null` when the section carries no finite geometry to measure against. Used as the
 * fallback for the `cs_xc` provenance axis / the plain-flap hinge mapping when the publisher did
 * not attach {@link SlottedSectionRig} provenance.
 */
export function sectionHingeXc(section: SlottedSection): number | null {
  if (!Number.isFinite(section.chord) || section.chord <= 0) return null;
  let xLe = Infinity;
  for (const el of section.elements) {
    for (const p of el.contour) {
      const x = p?.[0];
      if (typeof x === 'number' && Number.isFinite(x) && x < xLe) xLe = x;
    }
  }
  if (!Number.isFinite(xLe)) return null;
  const xc = (section.hinge.x - xLe) / section.chord;
  return Number.isFinite(xc) ? xc : null;
}
